import React, { useEffect, useMemo, useState } from "react";
import FiltersPagination from "../../../components/elements/FiltersPagination";
import DataTableElement from "../../../components/elements/DataTableElement";
import Columns from "./Columns";
import { paymentsResource } from "../../../helpers/configRoutes/backend_helper";
import { currencyUSD } from "../../../helpers/forms/formater";

const formatApiDateTime = (value, endOfDay = false) => {
    if (!value) return null;

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    const time = endOfDay ? '23:59:59' : '00:00:00';

    return `${year}-${month}-${day}T${time}`;
};

const Accounting = () => {
    const [filters, setFilters] = useState({
        endDate: null,
        startDate: null,
        search: '',
        from: 0,
        to: 0,
        page: 1
    });

    const [tableLoad, setTableLoad] = useState({
        show: false,
        textLoad: 'Loading sales...'
    });

    const [meta, setMeta] = useState({ total: 0, current_page: 0, from: 0, to: 0, last_page: 1 });
    const [data, setData] = useState([]);
    const [selectedRowId, setSelectedRowId] = useState(null);

    useEffect(() => {
        const getPayments = async () => {
            setTableLoad(prev => ({ ...prev, show: true }));

            const params = {
                ...filters,
                startDate: formatApiDateTime(filters.startDate),
                endDate: formatApiDateTime(filters.endDate, true),
            };

            const response = await paymentsResource('get', { params });

            setData(response?.payments || []);
            setMeta(response?.meta || { total: 0, current_page: 0, from: 0, to: 0, last_page: 1 });
            setTableLoad(prev => ({ ...prev, show: false }));
        };

        getPayments();
    }, [filters]);

    const cols = Columns(selectedRowId);

    const summary = useMemo(() => {
        const totalSales = data.reduce((acc, item) => acc + Number(item.total || 0), 0);
        const completedSales = data.filter(item => item.status?.name === 'Completed').length;

        return {
            totalSales,
            completedSales,
            visibleSales: data.length,
        };
    }, [data]);

    return (
        <div className="p-8 bg-gray-50 min-h-screen">
            <div className="flex flex-col gap-2 mb-6">
                <p className="text-sm uppercase tracking-[0.25em] text-mts-blue-primary font-semibold">Sales</p>
                <div className="text-2xl font-semibold">Sales overview</div>
                <p className="text-sm text-gray-500">Consulta únicamente las ventas registradas y filtra por rango de fechas para revisar los movimientos del periodo.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                    <p className="text-sm text-gray-500">Visible sales</p>
                    <p className="text-2xl font-semibold text-slate-900 mt-2">{summary.visibleSales}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                    <p className="text-sm text-gray-500">Completed sales</p>
                    <p className="text-2xl font-semibold text-slate-900 mt-2">{summary.completedSales}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                    <p className="text-sm text-gray-500">Visible amount</p>
                    <p className="text-2xl font-semibold text-slate-900 mt-2">{currencyUSD(summary.totalSales)}</p>
                </div>
            </div>

            <FiltersPagination
                showFilters={true}
                filters={filters}
                setFilters={setFilters}
                meta={meta}
                showPlanFilter={false}
                showStatusFilter={false}
                showDateFilter={true}
            >
                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                    <DataTableElement
                        columns={cols}
                        items={data}
                        tableLoad={tableLoad}
                        onRowClicked={(row) => setSelectedRowId(row.id)}
                        conditionalRowStyles={[
                            {
                                when: row => row.id === selectedRowId,
                                style: {
                                    backgroundColor: '#e0f2fe',
                                    cursor: 'pointer'
                                }
                            }
                        ]}
                    />
                </div>
            </FiltersPagination>
        </div>
    );
};

export default Accounting;
